import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';

import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import { formatBoolean } from '../common/util/formatter';
import { prefixString } from '../common/util/stringUtils';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionActions from './components/CollectionActions';
import { useManager, useRestriction } from '../common/util/permissions';
import useCrudExports from '../common/hooks/useCrudExports';

const CommandsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useTranslation();
  const manager = useManager();
  const limitCommands = useRestriction('limitCommands');

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/commands');
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas
  const stats = useMemo(() => {
    const total = items.length;
    const smsEnabled = items.filter((i) => i.textChannel).length;
    const smsDisabled = total - smsEnabled;
    return { total, smsEnabled, smsDisabled };
  }, [items]);

  // Filtros de status
  const statusFilters = [
    { value: '', label: t('sharedAll'), count: stats.total, color: theme.palette.primary.main, Icon: StorageIcon },
    { value: 'smsEnabled', label: t('commandSendSms'), count: stats.smsEnabled, color: theme.palette.success.main, Icon: CheckCircleIcon },
    { value: 'smsDisabled', label: t('sharedDisabled'), count: stats.smsDisabled, color: theme.palette.error.main, Icon: BlockIcon },
  ];

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'smsEnabled') {
      result = result.filter((i) => i.textChannel);
    } else if (statusFilter === 'smsDisabled') {
      result = result.filter((i) => !i.textChannel);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.description?.toLowerCase().includes(lower) ||
          i.type?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'description', headerNameKey: 'sharedDescription' },
    {
      field: 'type',
      headerNameKey: 'sharedType',
      valueGetter: ({ row }) => t(prefixString('command', row.type) || 'sharedType'),
    },
    {
      field: 'textChannel',
      headerNameKey: 'reportColumnTextChannel',
      valueGetter: ({ row }) => formatBoolean(row.textChannel, t),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportCommandsTitle',
    headerKey: 'reportCommandsHeader',
    rows: filteredItems,
    columns: exportColumns,
    theme,
    t,
    options: { includeWatermark: true },
  });

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedSavedCommands']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* CARDS DE ESTATÍSTICAS */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {statusFilters.map((f) => (
              <Paper
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                sx={{
                  flex: '1 1 200px',
                  p: 1.5,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: statusFilter === f.value ? f.color : 'divider',
                  bgcolor: statusFilter === f.value ? f.color + '10' : 'background.paper',
                  '&:hover': { boxShadow: 3, transform: 'translateY(-3px)' },
                  transition: 'all 0.2s',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: f.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <f.Icon sx={{ color: f.color, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">{f.count}</Typography>
                    <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* BARRA DE AÇÕES */}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 180 }}>
              {t('sharedSavedCommands')}
            </Typography>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              placeholder={t('sharedSearch')}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="info" size="small" startIcon={<AddCircleIcon />} onClick={() => navigate('/settings/command')} disabled={limitCommands}>{t('sharedAdd')}</Button>
              {manager && (
                <>
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>CSV</Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>Excel</Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>PDF</Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* TABELA */}
          <Paper sx={{ flex: 1, overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>ID</TableCell>
                    <TableCell>{t('sharedDescription')}</TableCell>
                    <TableCell>{t('sharedType')}</TableCell>
                    <TableCell>{t('commandSendSms')}</TableCell>
                    {!limitCommands && <TableCell>{t('sharedActions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={limitCommands ? 5 : 6} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow><TableCell colSpan={limitCommands ? 5 : 6} align="center" sx={{ py: 8 }}><Typography variant="body2" color="text.secondary">{t('sharedNoData')}</Typography></TableCell></TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                            {row.description?.charAt(0)?.toUpperCase() || 'C'}
                          </Avatar>
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.description || '-'}</TableCell>
                        <TableCell>{t(prefixString('command', row.type))}</TableCell>
                        <TableCell>
                          <Chip label={formatBoolean(row.textChannel, t)} color={row.textChannel ? 'success' : 'default'} size="small" />
                        </TableCell>
                        {!limitCommands && (
                          <TableCell>
                            <CollectionActions
                              itemId={row.id}
                              editPath="/settings/command"
                              endpoint="commands"
                              setTimestamp={setTimestamp}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default CommandsPage;
